import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MenuIcon from "@mui/icons-material/Menu";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

import Styles from "./Navbar.module.css";
import { ReactComponent as Gna } from "../../assets/images/logo.svg.svg";
import { publicEnergyType, publicRating } from "../../Redux/Slices/publicSlice";
import { userAccess } from "../../Redux/Slices/profileSlice";
import { walletAccount } from "../../Redux/Slices/paymentSlice";
import NavProfile from "./NavProfile";
import MobileNavbar from "./MobileNavbar";
import NotificationContainer from "./NotificationContainer/NotificationContainer";

const formatIndianNumber = (num) => {
  if (num == null) return "₹0";
  return "₹" + num.toLocaleString("en-IN");
};

const formatDateTime = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  return `${day} ${month} ${year}  ${hh}:${mm}:${ss}`;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ADMIN_ROLES = ["MAKER", "CHECKER", "ADMIN", "SUPER_ADMIN"];

const navList = {
  CONSUMER: [
    { name: "Home", url: "dashboard" },
    { name: "Bilateral Ledger", url: "bilateral" },
    { name: "Auction", url: "auction" },
    { name: "Search Listing", url: "search_listing" },
    { name: "Landing Cost Calculator", url: "lcc" },
    { name: "Past Listing", url: "past_listing_con_gen" },
    { name: "RFQ", url: "rfq" },
    { name: "TradeConnect", url: "tradeconnect" },
    { name: "REC", url: "rec" },
    { name: "BESS", url: "bess" },
    { name: "VPPA", url: "vppa" },
    { name: "Reverse Auction", url: "reverse_auction" },
    { name: "Tender Details", url: "tender_active" },
  ],
  GENERATOR: [
    { name: "Home", url: "dashboard" },
    { name: "Ledger", url: "bilateral" },
    { name: "Auction", url: "auction" },
    { name: "Search Listing", url: "search_listing" },
    { name: "Landing Cost Calculator", url: "lcc" },
    { name: "Past Listing", url: "past_listing_con_gen" },
    { name: "RFQ", url: "rfq" },
    { name: "TradeConnect", url: "tradeconnect" },
    { name: "VPPA", url: "vppa" },
    { name: "Reverse Auction", url: "reverse_auction" },
    { name: "BESS", url: "bess" },
    { name: "REC", url: "rec" },
    { name: "Tender Details", url: "tender_active" },
  ],
  BANKING: [
    { name: "Home", url: "banking" },
    { name: "Search Listing", url: "banking_search" },
    { name: "My Banking Ledger", url: "banking_ledger" },
  ],
  COMPANY_ADMIN: [],
};

// ─── Main Component ──────────────────────────────────────────────────────────

const Navbar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const token = useSelector((s) => s?.persistedReducer?.token);
  const role = useSelector((s) => s?.persistedReducer?.user?.activeRole);
  const activeEntity = useSelector((s) => s?.activeEntity?.activeEntity);
  const activeSidebarItem = useSelector(
    (s) => s?.activeEntity?.activeSidebarItem,
  );
  const profile = useSelector((s) => s?.profileSlice);
  const persistData = useSelector((s) => s?.persistedReducer);
  const companyName = persistData?.company?.companyName;
  const displayName = persistData?.company?.displayName;

  const walletData = useSelector((s) => s?.paymentSlice?.walletAccountActive);
  const userData = useSelector((s) => s?.persistedReducer?.user);

  const [drawer, setDrawer] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (token && !profile.userAccess) dispatch(userAccess(token));
    dispatch(publicRating());
    dispatch(publicEnergyType());
  }, [token, profile.userAccess, dispatch]);

  useEffect(() => {
    if (!role || !userData) return;
    if (role !== "CONSUMER" && role !== "GENERATOR") return;
    const id = userData?.activeGeneratorId || userData?.activeConsumerId;
    if (!id) return;
    dispatch(walletAccount({ role: role.toLowerCase(), id }));
  }, [dispatch, role, userData]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const showVidBalance = token;

  const showBankingTime = token && role === "BANKING";

  const getBrandInfo = () => {
    const path = location.pathname;
    const { activeConsumerId, activeGeneratorId, activeDiscomId } =
      persistData?.user ?? {};
    const pageName =
      navList[role]?.find((e) => `/${e.url}` === path)?.name ||
      activeSidebarItem ||
      "";

    if (path.includes("/my_profile"))
      return { entityName: companyName, subLabel: "My Profile" };
    if (path.includes("/wallet"))
      return { entityName: activeEntity, subLabel: "Escrow" };
    if (path.includes("/archive"))
      return { entityName: activeEntity || displayName, subLabel: "Archive" };
    if (path.includes("/unified_ledger"))
      return { entityName: activeEntity, subLabel: "Unified Ledger" };
    if (path.includes("/coming-soon"))
      return { entityName: "OTC Dashboard", subLabel: "Coming Soon" };

    if (role === "BANKING") {
      const entityName =
        persistData?.discom?.find((e) => e.discomId === activeDiscomId)
          ?.discomName ||
        persistData?.company?.displayName ||
        companyName;
      return { entityName, subLabel: pageName || "Banking" };
    }

    if (role === "CONSUMER" || role === "GENERATOR") {
      const entityName = activeConsumerId
        ? persistData?.consumer?.find((e) => e.consumerId === activeConsumerId)
            ?.consumerName
        : activeGeneratorId
          ? persistData?.generator?.find(
              (e) => e.generatorId === activeGeneratorId,
            )?.generatorName
          : activeDiscomId
            ? persistData?.discom?.find((e) => e.discomId === activeDiscomId)
                ?.discomName || displayName
            : companyName;
      return { entityName, subLabel: pageName };
    }

    return { entityName: companyName, subLabel: pageName || "OTC Dashboard" };
  };

  const getModePill = () => {
    if (role === "CONSUMER")
      return { label: "Buy", pillClass: Styles.modePillBuy, pulseDot: true };
    if (role === "GENERATOR")
      return { label: "Sell", pillClass: Styles.modePillSell, pulseDot: false };
    if (role === "BANKING")
      return {
        label: "Banking",
        pillClass: Styles.modePillBanking,
        pulseDot: false,
      };
    return null;
  };

  const brandInfo = getBrandInfo();
  const modePill = getModePill();

  return (
    <>
      {drawer && (
        <MobileNavbar open={drawer} handleClose={() => setDrawer(false)} />
      )}
      <div className={Styles.navbar_section}>
        {/* Desktop */}
        <div className={Styles.navbar_sub_container}>
          {/* White brand block */}
          <div className={Styles.brandBlock}>
            <Gna className={Styles.gnaLogo} />
            <div className={Styles.brandDivider} />
            <div className={Styles.brandLabel}>
              <span className={Styles.brandEntity}>{brandInfo.entityName}</span>
              <span className={Styles.brandSub}>{brandInfo.subLabel}</span>
            </div>
          </div>

          {/* Coloured right zone */}
          <div className={Styles.headerRight}>
            {modePill && (
              <div className={`${Styles.modePill} ${modePill.pillClass}`}>
                <span
                  className={`${Styles.modeDot} ${modePill.pulseDot ? Styles.modeDotPulse : ""}`}
                />
                {modePill.label}
              </div>
            )}
            <div className={Styles.hSpacer} />
            {showVidBalance && (
              <div className={Styles.vidCard}>
                {walletData != null && (
                  <div className={Styles.vidRow}>
                    <span className={Styles.vidBal}>BALANCE :</span>
                    <span className={Styles.vidValue}>
                      {formatIndianNumber(walletData?.amountRemaining ?? 0)}
                    </span>
                  </div>
                )}
                <div className={Styles.vidTime}>{formatDateTime(now)}</div>
              </div>
            )}
            {/* {showBankingTime && (
              <div className={Styles.vidCard}>
                <div className={Styles.vidTime}>{formatDateTime(now)}</div>
              </div>
            )} */}
            {token && !ADMIN_ROLES.includes(role) && <NotificationContainer />}
            {token && <NavProfile />}
          </div>
        </div>

        {/* Mobile */}
        <div
          className={`${Styles.navbar_sub_mobile_container} ${Styles.headerRight}`}
        >
          <div className={Styles.navbar_logo_main_section}>
            <div
              className={Styles.menuContainer}
              onClick={() => setDrawer(true)}
            >
              <MenuIcon style={{ color: "#fff" }} />
            </div>
            <div className={Styles.navabr_logo}>
              <Gna />
            </div>
          </div>
          <div className={Styles.currentPageName}>
            {
              navList[role]?.find((e) => `/${e.url}` === location.pathname)
                ?.name
            }
          </div>
          {token && <NavProfile />}
        </div>
      </div>
    </>
  );
};

export default Navbar;
